<script lang="ts" setup>
import { useDocument } from '@/composition'
import { useBuilderStore } from '@/store/builder'
import { IElement } from '@gohighlevel/ghl-proposals-common'
import { PropType, ref, computed, watch } from 'vue'
import RichTextEditor from '../RichTextEditor.vue'
import { useAppStore } from '@/store/app'

const store = useBuilderStore()
const { loadingCustomValues, documentVersion } = useDocument()
const appStore = useAppStore()

const props = defineProps({
  genKey: { type: String },
  element: {
    type: Object as PropType<IElement>,
  },
})

const refreshCount = ref(0)

const flatCustomValues = computed(() => ({
  ...store.flatCustomValues,
  ...appStore.flatDocumentVariables,
}))

watch(
  () => [flatCustomValues.value, loadingCustomValues.value],
  () => {
    refreshCount.value = 1 + refreshCount.value
  }
)

// Check if element version equals 1
const shouldUseDefaultFont = computed(() => {
  return documentVersion.value === 1
})

const isVersionGreaterThanOne = computed(() => {
  return documentVersion.value > 1
})

const customStyleForTextParent = computed(() => {
  const styles = props.element?.responsiveStyles.large
  return {
    backgroundColor: styles?.backgroundColor,
    marginTop: styles?.marginTop,
    marginBottom: styles?.marginBottom,
    marginLeft: styles?.marginLeft,
    marginRight: styles?.marginRight,
    paddingTop: styles?.paddingTop,
    paddingBottom: styles?.paddingBottom,
    paddingLeft: styles?.paddingLeft,
    paddingRight: styles?.paddingRight,
  }
})
</script>

<template>
  <div class="el-wrapper text-el">
    <div
      :style="customStyleForTextParent"
      class="rounded-smooth"
      :key="refreshCount"
      :class="{
        'document-version-eq-version-1': shouldUseDefaultFont,
        'document-version-gt-1': isVersionGreaterThanOne,
      }"
    >
      <RichTextEditor
        :custom-values="flatCustomValues"
        :gen-key="props.genKey"
        :element="props.element"
      />
    </div>
  </div>
</template>

<style lang="scss">
.editor-container {
  padding: 0px !important;
  min-width: unset !important;
  border-radius: unset !important;
  border: unset !important;
}
.editor-wrapper {
  .tiptap.ProseMirror[contenteditable='false'] {
    white-space: break-spaces !important;
  }
  p {
    break-inside: avoid-column !important;
    font-size: 16px;
  }
}

/* Add font-family only when element version equals 1; for backward compatibility */
.document-version-eq-version-1 {
  .editor-wrapper {
    p {
      font-family: sans-serif;
    }
  }
}

.document-version-gt-1 {
  .editor-wrapper {
    font-weight: 400;
    text-rendering: optimizeLegibility;
    line-height: 1.5;
    word-break: break-word;
    white-space: pre-wrap;
    /* newer spec: preserves every space + breaks at them */
    white-space: break-spaces !important;
    font-variant-ligatures: none;
    p {
      margin: 0;
      &[style*='text-align: justify'] {
        text-align-last: left;
      }
      &[style*='text-align: left'] {
        text-align-last: left;
      }
      &[style*='text-align: right'] {
        text-align-last: right;
      }
      &[style*='text-align: center'] {
        text-align-last: center;
      }

      span {
        /* fallback for older browsers */
        white-space: pre-wrap;
        /* newer spec: preserves every space + breaks at them */
        white-space: break-spaces !important;
        white-space-collapse: preserve; /* Chrome’s way of collapsing normal spaces */
        text-wrap-mode: wrap; /* Chrome-only */
        -webkit-nbsp-mode: normal; /* Safari-style breakable NBSP for both engines */
        // display: inline-block;
        max-width: 100%;
        word-break: break-word;
        display: inline;
        vertical-align: top;
      }

      a {
        display: inline;
      }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 0;
      a {
        display: inline;
      }

      span {
        /* keep your pre-wrap; then explicitly restore “break at spaces” */
        white-space: pre-wrap;
        /* newer spec: preserves every space + breaks at them */
        white-space: break-spaces !important;
        white-space-collapse: preserve; /* Chrome’s way of collapsing normal spaces */
        text-wrap-mode: wrap; /* Chrome-only */
        -webkit-nbsp-mode: normal; /* Safari-style breakable NBSP for both engines */
        // display: inline-block;
        max-width: 100%;
        word-break: break-word;
        display: inline;
        vertical-align: top;
      }
    }
  }
}
</style>
